import * as React from 'react';
import {
  Box, Typography, IconButton, Menu, MenuItem, Button, Grid, Card, CardContent, Paper,
} from '@material-ui/core';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import type { GridColumns } from '@material-ui/data-grid';
import { DataGrid } from '@material-ui/data-grid';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import globalUseStyles from '../../globalClasses';
import { colors } from '../../theme';
import type { ISkillLevelGroup } from './SkillLevelTypeInterfaces';

const useStyles = makeStyles(() => createStyles({
  textBlue: {
    color: colors.primary,
  },
}));

interface SkillLevelGroupLookupProps {
  data: ISkillLevelGroup[] | undefined,
  setInterfaceType: React.Dispatch<React.SetStateAction<string>>;
  addNewGroup: () => void;
  setEditSkillGroupFormValues: (value: ISkillLevelGroup) => void,
  setShowGroupsModal: (value: boolean) => void,
  deleteSkillLevelGroup: () => void,
}

const SkillLevelGroupLookup: React.FC<SkillLevelGroupLookupProps> = ({
  data, addNewGroup, setInterfaceType, setEditSkillGroupFormValues, setShowGroupsModal, deleteSkillLevelGroup,
}) => {
  const classes = useStyles();
  const globalStyles = globalUseStyles();

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [pageSize, setPageSize] = React.useState(10);

  const handleVertIconClick = (event: React.MouseEvent<HTMLButtonElement>, value: number) => {
    if (!anchorEl) {
      setAnchorEl(event.currentTarget);
      if (data?.filter((slt) => slt.value === value)[0]) {
        setEditSkillGroupFormValues(data?.filter((slt) => slt.value === value)[0]);
      }
    }
  };

  const tableColumns: GridColumns = [
    {
      field: 'name',
      headerName: 'Group Name',
      flex: 0.5,
      align: 'left',
    },
    {
      field: 'value',
      headerName: 'Actions',
      flex: 0.25,
      renderCell: ({ value }) => (
        ((typeof (value) === 'number')
          ? (
            <IconButton
              aria-label="more"
              aria-controls="long-menu"
              aria-haspopup="true"
              onClick={(event: React.MouseEvent<HTMLButtonElement>) => handleVertIconClick(event, value)}
            >
              <MoreVertIcon />
            </IconButton>
          )
          : <></>
        )
      ),
    },
  ];

  const tableRows = data && data?.map((slt, index) => ({
    value: slt.value || index,
    name: slt.name || '',
    id: slt.value,
  }));

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  return (
    <Paper>
      <Card>
        <Grid item xs={12}>
          <Box p={2}>
            <Grid container spacing={0}>
              <Grid item xs={8}>
                <Typography variant="h6" className={classes.textBlue} display="inline">{'Skill Level Groups'}</Typography>
              </Grid>
              <Grid item xs={4}>
                <Button fullWidth variant="contained" color="primary" onClick={() => addNewGroup()}>
                  { 'Add New Group' }
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <CardContent>
            <Box height={((pageSize + 1) * 40) + 60}>
              <DataGrid
                rows={tableRows || []}
                columns={tableColumns}
                pageSize={pageSize}
                rowsPerPageOptions={[10, 25, 50, 100]}
                loading={!data}
                onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                className={globalStyles.transparentDataGrid}
              />
            </Box>
            {anchorEl
            && (
              <Menu
                anchorEl={anchorEl}
                keepMounted
                open
                onClose={handleCloseMenu}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              >
                <MenuItem id="edit-skillLevelType-button" onClick={() => { setShowGroupsModal(true); handleCloseMenu(); setInterfaceType('Edit'); }}>{'Edit'}</MenuItem>
                <MenuItem id="delete-skillLevelType-button" onClick={() => { deleteSkillLevelGroup(); handleCloseMenu(); }}>{'Delete'}</MenuItem>
              </Menu>
            )}
          </CardContent>
        </Grid>
      </Card>
    </Paper>
  );
};

export default SkillLevelGroupLookup;
